import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  requestNotificationPermission, 
  onForegroundMessage, 
  isNotificationSupported,
  getNotificationPermissionStatus,
  initializeMessaging
} from '../lib/firebase';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export const usePushNotifications = () => {
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState('default');
  const [isEnabled, setIsEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [fcmToken, setFcmToken] = useState(null);

  const token = localStorage.getItem('token');

  // Check if notifications are supported
  useEffect(() => {
    const checkSupport = async () => {
      const supported = await isNotificationSupported();
      setIsSupported(supported);
      setPermission(getNotificationPermissionStatus());
      setIsLoading(false);
    };
    checkSupport();
  }, []);

  // Initialize messaging and listen for foreground messages
  useEffect(() => {
    const init = async () => {
      await initializeMessaging();
      
      // Listen for foreground messages
      const unsubscribe = onForegroundMessage((payload) => {
        // Show toast notification for foreground messages
        toast(payload.notification?.title || 'New Message', {
          description: payload.notification?.body,
          duration: 5000,
        });
      });

      return unsubscribe;
    };

    if (isSupported && permission === 'granted') {
      init();
    }
  }, [isSupported, permission]);

  // Check notification status from backend
  const checkStatus = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await axios.get(`${API}/api/push/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsEnabled(response.data.push_enabled);
    } catch (error) {
      console.error('Error checking notification status:', error);
    }
  }, [token]);

  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  // Enable push notifications
  const enableNotifications = async () => {
    if (!isSupported) {
      toast.error('Push notifications are not supported in this browser');
      return false;
    }

    setIsLoading(true);
    
    try {
      // Request permission and get FCM token
      const fcmTokenResult = await requestNotificationPermission();
      
      if (!fcmTokenResult) {
        toast.error('Unable to enable notifications. Please check your browser settings.');
        setIsLoading(false);
        return false;
      }

      setFcmToken(fcmTokenResult);
      setPermission('granted');

      // Register token with backend
      await axios.post(
        `${API}/api/push/fcm/register`,
        { token: fcmTokenResult, device_type: 'web' },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setIsEnabled(true);
      toast.success('Push notifications enabled!');
      setIsLoading(false);
      return true;
    } catch (error) {
      console.error('Error enabling notifications:', error);
      toast.error('Failed to enable notifications');
      setIsLoading(false);
      return false;
    }
  };

  // Disable push notifications
  const disableNotifications = async () => {
    if (!fcmToken) {
      setIsEnabled(false);
      return true;
    }

    setIsLoading(true);
    
    try {
      await axios.delete(`${API}/api/push/fcm/unregister?token=${fcmToken}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setIsEnabled(false);
      setFcmToken(null);
      toast.success('Push notifications disabled');
      setIsLoading(false);
      return true;
    } catch (error) {
      console.error('Error disabling notifications:', error);
      toast.error('Failed to disable notifications');
      setIsLoading(false);
      return false;
    }
  };

  // Send test notification
  const sendTestNotification = async () => {
    if (!token) return;
    
    try {
      const response = await axios.post(
        `${API}/api/push/send-test`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.status === 'sent') {
        toast.success(`Test notification sent to ${response.data.success_count} device(s)`);
      } else if (response.data.status === 'no_tokens') {
        toast.warning('No registered devices found. Enable notifications first.');
      } else {
        toast.error(response.data.message || 'Failed to send test notification');
      }
    } catch (error) {
      console.error('Error sending test notification:', error);
      toast.error('Failed to send test notification');
    }
  };

  return {
    isSupported,
    permission,
    isEnabled,
    isLoading,
    enableNotifications,
    disableNotifications,
    sendTestNotification,
    checkStatus
  };
};

export default usePushNotifications;
